import { inject, Injectable } from '@angular/core';
import { QueryDocumentSnapshot } from '@angular/fire/firestore';
import { BehaviorSubject, Observable } from 'rxjs';
import { map, shareReplay } from 'rxjs/operators';
import { PaginationResult } from '../../core/db/firestore.models';
import { toDate } from '../../core/utils';
import { AddressType, PostalAddress } from '../../global/models';
import { setDefaultAcceptanceToUser } from '../acceptance/acceptance.helpers';
import { Coupon, UserCoupon } from '../coupon';
import type { DraftOrder } from '../order/order.models';
import { injectUserApi, UserPasswordResetEmailRequest, UserPasswordResetEmailResponse } from './user-api';
import { draftOrderFirestoreConverter, referrerMonthlyTrackingConverter, UserDbService } from './user-db.service';
import { AccountData, MetaReferrer, ReferrerMonthlyTracking, UserModel, WhatsappSubscriptionStatus } from './user.models';

@Injectable({ providedIn: 'root' })
export class UserService {
  public user$ = new BehaviorSubject<UserModel>(null);
  public userG: UserModel;

  private userApi = injectUserApi();
  private userDbService = inject(UserDbService);

  /**
   * Emits current user state as a shared observable.
   * @returns Observable with current user.
   */
  get user(): Observable<any> {
    return this.user$.asObservable().pipe(
      shareReplay({ bufferSize: 1, refCount: true })
    );
  }

  /**
   * Updates current in-memory user state.
   * @param user Current authenticated user.
   */
  setCurrentUserState(user: any): void {
    this.user$.next(user);
    this.userG = user;
  }

  /**
   * Migrates legacy single-address fields to address arrays.
   * @param user User to sync.
   */
  syncUserLegacyAddresses(user: any): void {
    let updateData: Partial<UserModel>;

    if (user?.id) {
      // La primera vez que entren usuarios que no tengan el nuevo array de direcciones, se les añadirá
      // ese array con la dirección que ya tenían en shippingAddress o billingAddress. Además, como a partir de ahora
      // se usarán los arrays de direcciones de envío y facturación, se pondrán a null los campos shippingAddress y billingAddress.
      if (!user.shippingAddresses) {
        const addressesArray: PostalAddress[] = [];
        // Si en el antiguo shippingAddress había guardado un punto de recogida, no se añade al nuevo array de direcciones
        if (user.shippingAddress && !user.shippingAddress.pickupPointCode) {
          addressesArray.push(user.shippingAddress);
        }
        updateData = { shippingAddresses: addressesArray, defaultShippingAddressIndex: 0, shippingAddress: null };
        this.setUser({ id: user.id, ...updateData });
      }

      if (!user.billingAddresses) {
        const addressesArray: PostalAddress[] = [];
        // Si en el antiguo billingAddress había guardado un punto de recogida, no se añade al nuevo array de direcciones
        if (user.billingAddress && !user.billingAddress.pickupPointCode) {
          addressesArray.push(user.billingAddress);
        }
        updateData = { billingAddresses: addressesArray, defaultBillingAddressIndex: 0, billingAddress: null };
        this.setUser({ id: user.id, ...updateData });
      }
    }
  }

  /**
   * Updates user profile fields.
   * @param data Partial user payload including `id`.
   * @returns Promise resolved when update finishes.
   */
  setUser(data: Partial<UserModel> & Pick<UserModel, 'id'>): Promise<void> {
    return this.userDbService.update<UserModel>(data.id, data as UserModel);
  }

  /**
   * Creates a user record with initial acceptance values.
   * @param data User payload.
   * @returns Promise with created user ID.
   */
  createUser(data: Partial<UserModel>): Promise<string> {
    data = setDefaultAcceptanceToUser(data);
    return this.userDbService.create<UserModel>(data);
  }

  /**
   * Sends password reset email from backend using custom template.
   * @param request Password reset request payload.
   * @returns Promise resolved with backend function response.
   */
  UserResetPasswordInit(request: UserPasswordResetEmailRequest): Promise<UserPasswordResetEmailResponse> {
    return this.userApi.passwordResetEmail(request);
  }

  /**
   * Gets user by UID in real time.
   * @param uid Firebase Auth UID.
   * @returns Observable with user or `null`.
   */
  getUserByUid$(uid: string): Observable<UserModel | null> {
    return this.userDbService.getOneWhere$({ where: { uid } }).pipe(
      map(user => user ?? null)
    );
  }

  /**
   * Gets user by UID.
   * @param uid Firebase Auth UID.
   * @returns User or `null`.
   */
  getUserByUid(uid: string): Promise<UserModel | null> {
    return this.userDbService.getOneWhere({ where: { uid } }).then(user => user ?? null);
  }

  /**
   * Upserts a user on login by UID.
   * If user exists, updates selected profile fields.
   * If user does not exist, creates a new user record.
   * @param data Auth user payload.
   * @returns Promise resolved with the existing or newly created user.
   */
  upsertUserLogin(data: Partial<UserModel>): Promise<UserModel> {
    return this.getUserByUid(data.uid)
      .then(async user => {
        if (!user) {
          data = setDefaultAcceptanceToUser(data);
          const userId = await this.userDbService.create<UserModel>({ ...data });
          const newUser = { ...data, id: userId } as UserModel;
          return newUser;
        } else {
          let uData: Partial<UserModel> = {};
          if (data.phoneNumber) uData.phoneNumber = data.phoneNumber;
          uData.emailVerified = data.emailVerified;
          if (data.firstName) uData.firstName = data.firstName;
          if (data.lastName) uData.lastName = data.lastName;
          if (data.photoURL) uData.photoURL = data.photoURL;
          if (user.acceptance) uData.acceptance = user.acceptance;

          uData = setDefaultAcceptanceToUser(uData);
          await this.userDbService.update<UserModel>(user.id, uData);
          return user;
        }
      });
  }

  /**
   * Gets user by email.
   * @param email User email.
   * @returns User or `null`.
   */
  getUserByEmail(email: string): Promise<UserModel | null> {
    return this.userDbService.getOneWhere({ where: { email } })
      .then(user => user ?? null)
      .catch(err => {
        console.error(err);
        return null;
      });
  }

  /**
   * Gets user by public slug in real time.
   * @param slug User slug.
   * @returns Observable with user or `null`.
   */
  getUserBySlug$(slug: string): Observable<UserModel | null> {
    return this.userDbService.getOneWhere$({ where: { slug } }).pipe(
      map(user => user ?? null)
    );
  }

  /**
   * Gets featured creators.
   * @param limit Max number of creators.
   * @returns Observable with users.
   */
  getFeaturedCreatorUsers$(limit: number): Observable<UserModel[]> {
    return this.userDbService.getWhere$({
      where: [{ isCreator: true }, { featured: true }],
      limit
    });
  }

  /**
   * Upserts current user referrer metadata.
   * @param data Referrer metadata.
   * @returns Promise with upserted document ID.
   */
  setUserMetaReferrer(data: MetaReferrer): Promise<string> {
    const { id, ..._data } = data as any;
    return this.userDbService.upsert<MetaReferrer>({ id: 'referrer', ..._data }, { subPath: `${this.userG.id}/meta` });
  }

  /**
   * Gets referrer metadata for current user.
   * @returns Metadata or `null`.
   */
  async getUserMetaReferrer(): Promise<MetaReferrer | null> {
    try {
      const data = await this.userDbService.get<MetaReferrer>('referrer', { subPath: `${this.userG.id}/meta` });
      return data ? { ...data } : null;
    } catch {
      return null;
    }
  }

  /**
   * Gets referrer metadata in real time.
   * @param userId User ID.
   * @returns Observable with metadata or `null`.
   */
  getUserMetaReferrer$(userId: string): Observable<MetaReferrer | null> {
    return this.userDbService.get$<MetaReferrer>('referrer', { subPath: `${userId}/meta` }).pipe(
      shareReplay({ bufferSize: 1, refCount: true })
    );
  }

  /**
   * Returns a live stream of product IDs assigned as ambassador products for a user.
   * @param userId User ID.
   * @returns Observable emitting the list of ambassador product IDs.
   */
  getAmbassadorProductIds$(userId: string): Observable<string[]> {
    return this.userDbService.get$<UserModel>(userId).pipe(
      map(user => (user?.ambassadorProducts ?? []).map((amb: { productId: string; }) => amb.productId))
    );
  }

  /**
   * Gets latest monthly tracking for a referrer.
   * @param userId User ID.
   * @returns Observable with tracking item or `null`.
   */
  getReferrerMonthlyTracking$(userId: string): Observable<ReferrerMonthlyTracking | null> {
    return this.userDbService.getOneWhere$<any>(
      { orderBy: { date: 'desc' } },
      { subPath: `${userId}/referrerMonthlyTracking`, converter: referrerMonthlyTrackingConverter }
    ).pipe(map(item => (item as ReferrerMonthlyTracking) ?? null));
  }

  /**
   * Creates a draft order for a user.
   * @param userId User ID.
   * @param draftOrder Draft order payload.
   * @returns Promise resolved with created draft ID.
   */
  createUserDraftOrder(userId: string, draftOrder: DraftOrder): Promise<string> {
    const { id, ...payload } = draftOrder;
    return this.userDbService.create<DraftOrder>(payload as DraftOrder, { subPath: `${userId}/draftOrders` });
  }

  /**
   * Gets draft orders for a user.
   * @param userId User ID.
   * @param pageSize Optional max number of records.
   * @param startAfterDoc Optional snapshot cursor for next page.
   * @returns Observable with paginated draft orders.
   */
  getUserDraftOrders$(
    userId: string,
    pageSize?: number,
    startAfterDoc: QueryDocumentSnapshot<DraftOrder> | null = null
  ): Observable<PaginationResult<DraftOrder>> {
    return this.userDbService.paginate$<DraftOrder>(
      {
        orderBy: { createdAt: 'desc' },
        limit: pageSize ?? 100,
        ...(startAfterDoc ? { startAfter: startAfterDoc } : {})
      },
      {
        subPath: `${userId}/draftOrders`,
        converter: draftOrderFirestoreConverter
      }
    );
  }
  /**
   * Deletes one draft order for a user.
   * @param userId User ID.
   * @param draftOrderId Draft order ID.
   * @returns Promise resolved when delete finishes.
   */
  deleteUserDraftOrder(userId: string, draftOrderId: string): Promise<void> {
    return this.userDbService.delete<DraftOrder>(draftOrderId, { subPath: `${userId}/draftOrders` });
  }

  /**
   * Gets first user coupon from `users/{id}/usercoupons`.
   * @param userId User ID.
   * @returns First coupon or `null`.
   */
  async getUserCoupon(userId: string): Promise<Coupon | null> {
    const coupon = await this.userDbService.getOneWhere<Coupon>({ limit: 1 }, { subPath: `${userId}/usercoupons` });
    return coupon ?? null;
  }

  /**
   * Gets all user coupons from `users/{id}/usercoupons`.
   * @param userId User ID.
   * @returns Coupon list with normalized `startDate` and `expirationDate`.
   */
  async getUserCoupons(userId: string): Promise<UserCoupon[]> {
    const coupons = await this.userDbService.getAll<UserCoupon>({ subPath: `${userId}/usercoupons` });
    return coupons.map(coupon => ({
      ...coupon,
      startDate: toDate((coupon).startDate),
      expirationDate: toDate((coupon).expirationDate)
    }));
  }

  /**
   * Update user account data form fields in My Profile section: displayName, email and phoneNumber
   * @param dataFormValue Object with form values for displayName, email and phoneNumber
   * @returns Promise resolved when update finishes.
   */
  updateAccountData(dataFormValue: AccountData): Promise<void> {
    const updatedUser = { ...this.userG, ...dataFormValue };
    return this.setUser(updatedUser);
  }

  /**
   * Adds an address to the user's address list.
   * @param address The address to add.
   * @param type The type of address ('shipping' or 'billing').
   * @param setAsDefault Whether to set this address as the default.
   * @returns Promise resolved when the update finishes.
   */
  addAddressToUser(address: PostalAddress, type: AddressType, setAsDefault: boolean): Promise<void> {
    const addresses = this.userG[`${type}Addresses`] || [];
    addresses.push(address);
    let updateData: { [key: string]: PostalAddress[] | number; } = { [`${type}Addresses`]: addresses };

    let defaultIndexKey = `default${type.charAt(0).toUpperCase() + type.slice(1)}AddressIndex`;
    if (addresses.length === 1) {
      updateData = { ...updateData, [defaultIndexKey]: 0 };
    }
    if (setAsDefault) {
      updateData = { ...updateData, [defaultIndexKey]: addresses.length - 1 };
    }
    return this.setUser({ ...this.userG, ...updateData });
  }

  /**
   * Updates an address in the user's address list.
   * @param updatedAddress The updated address.
   * @param type The type of address ('shipping' or 'billing').
   * @param index The index of the address to update.
   * @param setAsDefault Whether to set this address as the default.
   * @returns Promise resolved when the update finishes.
   */
  updateUserAddress(updatedAddress: PostalAddress, type: AddressType, index: number, setAsDefault: boolean): Promise<void> {
    const addresses = this.userG[`${type}Addresses`] || [];
    addresses[index] = updatedAddress;
    let updateData: { [key: string]: PostalAddress[] | number; } = {
      [`${type}Addresses`]: addresses
    };

    let defaultIndexKey = `default${type.charAt(0).toUpperCase() + type.slice(1)}AddressIndex`;
    if (setAsDefault) {
      updateData = { ...updateData, [defaultIndexKey]: index };
    }
    return this.setUser({ ...this.userG, ...updateData });
  }

  /**
   * Deletes an address from the user's address list.
   * @param type The type of address ('shipping' or 'billing').
   * @param index The index of the address to delete.
   * @returns Promise resolved when the update finishes.
   */
  deleteUserAddress(type: AddressType, index: number): Promise<void> {
    const addresses = this.userG[`${type}Addresses`] || [];
    if (index < 0 || index >= addresses.length) {
      return Promise.reject(new Error('Invalid address index'));
    }
    let defaultIndexKey = `default${type.charAt(0).toUpperCase() + type.slice(1)}AddressIndex`;
    let defaultIndex = this.userG[defaultIndexKey];

    addresses.splice(index, 1);
    let updateData: { [key: string]: PostalAddress[] | number; } = {
      [`${type}Addresses`]: addresses
    };

    // Si se han eliminado todas las direcciones
    if (addresses.length === 0) {
      updateData = { ...updateData, [defaultIndexKey]: null };
    }

    // Si solo queda una dirección, se establece como predeterminada
    else if (addresses.length === 1) {
      updateData = { ...updateData, [defaultIndexKey]: 0 };
    }

    // Si se ha eliminado la última dirección y esta era predeterminada
    else if (defaultIndex >= addresses.length) {
      updateData = { ...updateData, [defaultIndexKey]: addresses.length - 1 };
    }

    return this.setUser({ ...this.userG, ...updateData });
  }

  /**
   * Sets the default address for the user.
   * @param type The type of address ('shipping' or 'billing').
   * @param index The index of the address to set as default.
   * @returns Promise resolved when the update finishes.
   */
  setDefaultUserAddress(type: AddressType, index: number): Promise<void> {
    let defaultIndexKey = `default${type.charAt(0).toUpperCase() + type.slice(1)}AddressIndex`;
    return this.setUser({ ...this.userG, [defaultIndexKey]: index });
  }

  /**
   * Unsubscribes a user from WhatsApp notifications.
   * @param userId User ID.
   */
  async unsubscribeFromWhatsapp(userId: string): Promise<void> {
    try {
      const user = await this.userDbService.get<UserModel>(userId);
      if (!user) return;

      if (!user.whatsappSubscriptionStatus || user.whatsappSubscriptionStatus === WhatsappSubscriptionStatus.SUBSCRIBED) {
        await this.userDbService.update<UserModel>(userId, {
          whatsappSubscriptionStatus: WhatsappSubscriptionStatus.UNSUBSCRIBED
        } as Partial<UserModel>);
      }
    } catch (e) {
      console.error('Error unsubscribing from WhatsApp:', e);
    }
  }
}
